import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faFileExport, faFileImport, faGripLinesVertical, faPencilAlt, faPlus, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FileUploadModule, FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { BatchRecord } from 'src/app/controls/inline-editable-table/models/BatchRecord';
import { IEditableRightInterface } from 'src/app/controls/inline-editable-table/models/IEditableRightInterface';
import { InputDialogComponent } from 'src/app/controls/input-dialog/input-dialog.component';
import { InputDialogResult } from 'src/app/controls/input-dialog/models/input-dialog-result';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { Attribute } from 'src/app/models/attribute';
import { AttributeValue } from 'src/app/models/attribute-value';
import { Rights } from 'src/app/models/enums/rights';
import { AttributesValuesService } from 'src/app/services/attributes-values.service';
import { CrudService } from 'src/app/services/crud-service';
import { MemoryService } from 'src/app/services/memory.service';
import { AttributeFormContext } from '../attribute-form-context';

@Component({
    selector: 'app-attribute-value-form',
    templateUrl: './attribute-value-form.component.html',
    styleUrls: ['./attribute-value-form.component.scss'],
    imports: [CdkDropList, CdkDrag, FaIconComponent, CdkDragHandle, MatButtonModule, FileUploadModule],
})
export class AttributeValueFormComponent extends CrudService implements OnInit {
    public faGripLinesVertical = faGripLinesVertical;
    public faDelete = faTrash;
    public faEdit = faPencilAlt;
    public faSave = faSave;
    public faAdd = faPlus;
    public faFileimport = faFileImport;
    public faFileExport = faFileExport;

    /** Pour être utilisable dans le template */
    FormMode: any = FormMode;

    public attributeValues: BatchRecord<AttributeValue>[] = [];

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: AttributeFormContext = null;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<AttributeFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(context: AttributeFormContext) {
        this._context$.next(context);
    }

    @Output()
    public attributeValuesUpdated: EventEmitter<BatchRecord<AttributeValue>[]> = new EventEmitter();

    @Output()
    public needAttributeValuesRefreshed: EventEmitter<number> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    public aRights: IEditableRightInterface = {
        Create: false,
        Edit: false,
        Delete: false,
    };

    // Accès au enfant de type fileToUpload
    @ViewChild('fileToUpload', { static: true })
    fileToUploadVariable: ElementRef;

    // Composant FileUploaderCustom
    public uploader: FileUploader = null;

    constructor(
        public dialog: MatDialog,
        public enumHelper: EnumHelper,
        private attributesValuesService: AttributesValuesService,
        public override memoryService: MemoryService
    ) {
        super(memoryService);

        if (!!this.memoryService.getConnectedUser().hasRight(Rights.AttributCreate)) {
            this.aRights.Create = true;
        }
        if (!!this.memoryService.getConnectedUser().hasRight(Rights.AttributEdit)) {
            this.aRights.Edit = true;
        }
        if (!!this.memoryService.getConnectedUser().hasRight(Rights.AttributDelete)) {
            this.aRights.Delete = true;
        }
    }

    /**
     * Initialise le composant.
     */
    ngOnInit() {
        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;

            let attribute = this._context.item as Attribute;
            this.loadAttributeValues(attribute);
        });

        this.uploader = new FileUploader({
            url: `${this.baseUrl}/api/organizations/${this.organizationId}/views/${this.viewId}/attributes/${this._context.item.id}/attributeValue/import`,
            authTokenHeader: 'Authorization',
            authToken: `Bearer ${this.memoryService.getConnectedUser().token.accessToken}`,
            allowedFileType: ['xls'],
            queueLimit: 1,
        });

        this.uploader.onAfterAddingFile = (fileItem) => {
            fileItem.withCredentials = false;
        };

        this.uploader.onCompleteAll = () => {
            this.needAttributeValuesRefreshed.emit(this._context.item.id);
        };
    }

    private loadAttributeValues(attribute: Attribute) {
        this.attributeValues.splice(0); // Vide le tableau.

        let sortableValues = attribute.attributeValueDefinitions.sort((a, b) => {
            return a.order < b.order ? -1 : 1;
        });

        for (let i = 0; i < sortableValues.length; ++i) {
            let record = new BatchRecord<AttributeValue>(sortableValues[i]);
            this.attributeValues.push(record);
        }
    }

    public onFileSelected() {
        this.uploader.uploadAll();
    }

    /**
     * Sauvegarde les valeurs de l'attribut en BDD.
     */
    public saveAttributeValues() {
        this.attributeValuesUpdated.emit(this.attributeValues);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icone supprimer d'une valeur d'attribut.
     * @param attributeValueName Nom de la valeur d'attribut à supprimer.
     */
    public onRemoveAttributeValue(attributeValueName: string) {
        let records = [...this.attributeValues];

        let deletedItem = records.find((x) => x.data.name == attributeValueName);
        deletedItem.isDeleted = true;

        let index = 1;
        records.filter((x) => x.isDeleted !== true).forEach((x) => (x.data.order = index++));

        this.attributeValues.splice(0);
        records.forEach((x) => this.attributeValues.push(x));
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icone éditer d'une valeur d'attribut.
     * @param attributeValueName Nom de la valeur d'attribut à éditer.
     */
    public onEditAttributeValue(attributeValueName: string) {
        const dialogRef = this.dialog.open(InputDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Édition du nom de la variable d'attribut",
                label: 'Nom',
                value: attributeValueName,
            },
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: InputDialogResult) => {
            if (dialogResult.confirmation) {
                // Mise à jour du nom de l'état.
                this.editAttributeValueName(attributeValueName, dialogResult.value);
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Ajouter une valeur d'attribut'.
     */
    public onAddAttributeValue() {
        const dialogRef = this.dialog.open(InputDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une valeur d'attribut",
                label: 'Nom',
            },
        });

        // Attente de la réponse de la boite de dialogue de saisie
        dialogRef.afterClosed().subscribe((dialogResult: InputDialogResult) => {
            if (dialogResult.confirmation) {
                // Création de l'état.
                this.addAttributeValue(dialogResult.value);
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur fait un glisser/deposer d'une valeur d'attribut.
     * @param event Événement contenant les informations du glisser/deposer.
     */
    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.attributeValues, event.previousIndex, event.currentIndex);
        let index = 1;
        this.attributeValues
            .filter((x) => x.isDeleted !== true)
            .forEach((x) => {
                if (x.data.order !== index) {
                    x.data.order = index;
                    x.isEdited = true;
                }

                index++;
            });
    }

    /**
     * Modifie le nom d'une valeur d'attribut.
     * @param oldName ancien nom de la valeur d'attribut.
     * @param newName Nouveau nom de la valeur d'attribut.
     */
    editAttributeValueName(oldName: string, newName: string) {
        if (oldName === newName) {
            // Pas de changement de nom
            return;
        }

        if (this.attributeValues.filter((x) => x.isDeleted !== true).find((x) => x.data.name == newName)) {
            // Un état avec le même nom existe déjà.
            // --> erreur
            return;
        }

        // Changement de nom
        let editedItem = this.attributeValues.find((x) => x.data.name == oldName);
        editedItem.data.name = newName;
        editedItem.isEdited = true;
    }

    /**
     * Ajoute une valeur d'attribut à la liste des valeurs d'attributs.
     * @param attributeValueName Nom de la valeur d'attribut à ajouter
     */
    addAttributeValue(attributeValueName: string) {
        if (this.attributeValues.filter((x) => x.isDeleted !== true).find((x) => x.data.name == attributeValueName)) {
            // Un état avec le même nom existe déjà.
            // --> erreur
            return;
        }

        // Ajout de l'état
        let newAttributeValue = new AttributeValue();
        newAttributeValue.attributeId = this._context.item.id;
        newAttributeValue.name = attributeValueName;
        newAttributeValue.order = this.attributeValues.filter((x) => x.isDeleted !== true).length + 1;

        let record = new BatchRecord<AttributeValue>(newAttributeValue);
        record.isNew = true;

        this.attributeValues.push(record);
    }

    public onAttributeValuesExported() {
        this.attributesValuesService
            .Export(this._context.item.id)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = `Valeurs de l'attribut ${this._context.item.id}.xlsx`;
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
